import { useMemo, useState, useEffect } from "react";
import { 
  useProductBarcodesQuery,
  useStockQuery,
  useProductsQuery,
  useLocationsQuery,
  useQuantityUnitsQuery
} from "./useQueries.ts";
import { createBarcodeMap, createProductItemsMap } from "utils/stockDataProcessing.ts";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "./queryKeys.ts";
import { getAIExcludedLocations, getAIExcludedProducts } from "utils/storage.ts";
import { ProductItemWithDetails } from "utils/grocyApi.ts";

/**
 * Custom hook that encapsulates all stock data queries and processing
 * Suspense variant: returns ready data and a refresh helper
 */
export function useStockData() {
  // Get all required data using centralized queries
  const barcodesQuery = useProductBarcodesQuery();
  const stockQuery = useStockQuery();
  const productsQuery = useProductsQuery();
  const locationsQuery = useLocationsQuery();
  const unitsQuery = useQuantityUnitsQuery();

  const queryClient = useQueryClient();

  // Create barcode lookup map
  const barcodeMap = useMemo(() => {
    return createBarcodeMap(barcodesQuery.data);
  }, [barcodesQuery.data]);

  // Create product items lookup map (all products regardless of stock)
  const productItemsMap = useMemo(() => {
    return createProductItemsMap(
      stockQuery.data,
      productsQuery.data,
      locationsQuery.data,
      unitsQuery.data,
      true // Include all products regardless of stock
    );
  }, [stockQuery.data, productsQuery.data, locationsQuery.data, unitsQuery.data]);

  // Create stock items lookup map for consume operations (only products with stock > 0)
  const consumeStockItemsMap = useMemo(() => {
    return createProductItemsMap(
      stockQuery.data,
      productsQuery.data,
      locationsQuery.data,
      unitsQuery.data,
      false // Only include products with stock > 0
    );
  }, [stockQuery.data, productsQuery.data, locationsQuery.data, unitsQuery.data]);

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.productBarcodes });
    queryClient.invalidateQueries({ queryKey: queryKeys.stock });
    queryClient.invalidateQueries({ queryKey: queryKeys.products });
    queryClient.invalidateQueries({ queryKey: queryKeys.locations });
    queryClient.invalidateQueries({ queryKey: queryKeys.quantityUnits });
  };

  return {
    barcodeMap,
    productItemsMap,
    consumeStockItemsMap,
    units: unitsQuery.data,
    refreshAll,
  };
} 

/**
 * Custom hook that provides stock data filtered by AI location exclusions
 * Used specifically for AI suggestions and recipe details
 */
export function useAIFilteredStockData() {
  const { consumeStockItemsMap } = useStockData();
  
  // Use state to track excluded locations and products and trigger re-renders when they change
  const [excludedLocations, setExcludedLocations] = useState<Set<number>>(() => getAIExcludedLocations());
  const [excludedProducts, setExcludedProducts] = useState<Set<number>>(() => getAIExcludedProducts());

  // Listen for storage changes
  useEffect(() => {
    const checkForChanges = () => {
      const currentLocations = getAIExcludedLocations();
      const currentProducts = getAIExcludedProducts();
      setExcludedLocations(currentLocations);
      setExcludedProducts(currentProducts);
    };

    // Check on mount and when window gains focus
    checkForChanges();
    window.addEventListener('focus', checkForChanges);
    
    return () => {
      window.removeEventListener('focus', checkForChanges);
    };
  }, []);

  const filteredStockItemsMap = useMemo(() => {
    if (excludedLocations.size === 0 && excludedProducts.size === 0) {
      // If no locations or products are excluded, return all stock items
      return consumeStockItemsMap;
    }

    // Filter out items from excluded locations and products
    const filtered = new Map<number, ProductItemWithDetails>();
    for (const [productId, stockItem] of consumeStockItemsMap) {
      if (!excludedLocations.has(stockItem.location.id) && !excludedProducts.has(productId)) {
        filtered.set(productId, stockItem);
      }
    }
    return filtered;
  }, [consumeStockItemsMap, excludedLocations, excludedProducts]);

  return {
    stockItemsMap: filteredStockItemsMap,
    excludedLocations,
  };
} 